import { useEffect, useState } from 'react';
import { CameraAltOutlined, DeleteOutlineOutlined } from '@mui/icons-material';
import { Backdrop, Box, Button, Grid2, IconButton, Stack } from '@mui/material';
import { Webcam } from '@zenput/feature-zencam';
import { useSnackbar } from 'notistack';
import { makeStyles } from 'tss-react/mui';
import { ZenputFiles } from '../../../backbone/shared/models/files';
import {
    collectMediaWeb,
    collectMediaNative,
    collectCameraNative,
    getZenputFiles
} from '../../../backbone-unorganized/followUpActions/models';
import { createUUID } from '../../../backbone-unorganized/utils/files';
import { MIME_TYPE_TO_EXTENSION } from '../../../constants';
import { isWeb } from '../../../shared/utils/is';
import { ImageItem } from '../../shared/ImageItem';
import { ImageVideoPickerDialog } from '../../shared/ImageVideoPickerDialog';
import { SimpleGallery } from '../../shared/SimpleGallery/SimpleGallery';
import { FollowUpActionFileType, WebMediaResponseType, ZenputFileType } from './FollowUpActions.types';

interface FollowUpActionSavedGalleryProps {
    getStorageFullUrl: (value: string) => string;
    handleDeletePendingS3Keys: (index: number) => void;
    handleDeleteS3Keys: (key: string) => void;
    handlePendingAddS3Keys: (keys: string[]) => void;
    onChange: (images: FollowUpActionFileType[]) => void;
    s3keys: string[];
    uploadFollowUpFiles: (files: ZenputFileType[]) => string[];
    value: FollowUpActionFileType[];
}

const useStyles = makeStyles()((theme) => {
    const { spacing } = theme;

    return {
        imgBtn: {
            border: 'dashed',
            borderWidth: 'thin',
            color: 'gray',
            width: '-webkit-fill-available',
            padding: spacing(1),
            textTransform: 'none'
        },
        boxImageContainer: {
            height: spacing(14),
            width: spacing(14)
        },
        imgDiv: {
            display: 'flex'
        },
        imgPreview: {
            borderRadius: spacing(0.5),
            height: '100%',
            objectFit: 'cover',
            width: '100%'
        }
    };
});

export const FollowUpActionSavedGallery = (props: FollowUpActionSavedGalleryProps) => {
    const {
        getStorageFullUrl,
        handleDeletePendingS3Keys,
        handleDeleteS3Keys,
        handlePendingAddS3Keys,
        onChange,
        uploadFollowUpFiles,
        value,
        s3keys
    } = props;
    const { classes } = useStyles();
    const { enqueueSnackbar } = useSnackbar();

    const [combinedFiles, setCombinedFiles] = useState([]);
    const [galleryOpen, toggleGallery] = useState(false);
    const [openCamera, toggleOpenCamera] = useState(false);
    const [showImageDialog, setShowImageDialog] = useState(false);
    const [startIndex, setStartIndex] = useState(0);

    useEffect(() => {
        setCombinedFiles([...(s3keys ?? []), ...(value ?? [])]);
    }, [s3keys, value]);

    const handleToggleGallery = (index: number) => {
        setStartIndex(index);
        toggleGallery(true);
    };

    const removeImage = (index: number) => {
        onChange(value.filter((_, i) => i !== index));
        handleDeletePendingS3Keys(index);
    };

    const handleOnSelect = async (option: string) => {
        setShowImageDialog(false);
        if (!option) return;

        if (option === 'browse') {
            if (isWeb()) {
                const media: WebMediaResponseType = await collectMediaWeb();

                let keys;
                try {
                    keys = await uploadFollowUpFiles(media.files);
                    handlePendingAddS3Keys(keys);
                    onChange([...value, ...media.displayUrls]);
                } catch (error) {
                    enqueueSnackbar(_t('mobile_dialog_image_preview_message'), {
                        variant: 'error'
                    });
                    keys = [];
                }
            } else {
                const media = await collectMediaNative();
                const files = getZenputFiles(media.map((f) => f.path));

                let keys;
                try {
                    keys = await uploadFollowUpFiles(files);
                    handlePendingAddS3Keys(keys);
                    const displayUrls = media.map((f) => ({
                        name: f.path ? f.path.split('/').pop() : `temp.${f.format}`,
                        raw_file: f.webPath,
                        type: f.format
                    }));
                    onChange([...value, ...displayUrls]);
                } catch (error) {
                    enqueueSnackbar(_t('mobile_dialog_image_preview_message'), {
                        variant: 'error'
                    });
                    keys = [];
                }
            }
        }

        if (option === 'camera') {
            if (isWeb()) {
                toggleOpenCamera(true);
            } else {
                const photo = await collectCameraNative();
                let keys;
                try {
                    keys = await uploadFollowUpFiles(getZenputFiles([photo.path]));
                    handlePendingAddS3Keys(keys);
                    const displayUrls = {
                        name: photo.path ? photo.path.split('/').pop() : `temp.${photo.format}`,
                        raw_file: photo.webPath,
                        type: photo.format
                    };
                    onChange([...value, displayUrls]);
                } catch (error) {
                    enqueueSnackbar(_t('mobile_dialog_image_preview_message'), {
                        variant: 'error'
                    });
                    keys = [];
                }
            }
        }
    };

    return (
        <Stack>
            <Button
                className={classes.imgBtn}
                component="label"
                onClick={() => setShowImageDialog(true)}
                startIcon={<CameraAltOutlined />}
            >
                {_t('mobile_field_image_button_add')}
            </Button>
            {galleryOpen && (
                <SimpleGallery
                    items={combinedFiles.map((src, index) => {
                        const file = src.raw_file || getStorageFullUrl(src);
                        return { id: `${index}-${file}`, src: file };
                    })}
                    onClose={() => toggleGallery(false)}
                    startIndex={startIndex}
                />
            )}

            {(s3keys.length > 0 || value.length > 0) && (
                <Grid2 container direction="row" spacing={2} sx={{ marginTop: 2, marginBottom: 2 }}>
                    {s3keys.map((src, index) => (
                        <Grid2 key={`s3-${index}`}>
                            <Box className={classes.boxImageContainer} sx={{ position: 'relative' }}>
                                <ImageItem
                                    alt={`s3-preview ${index}`}
                                    className={classes.imgPreview}
                                    onClick={() => handleToggleGallery(index)}
                                    src={getStorageFullUrl(src)}
                                />
                                <IconButton
                                    size="small"
                                    onClick={() => handleDeleteS3Keys(src)}
                                    sx={{
                                        bgcolor: 'red',
                                        color: 'white',
                                        '&:hover': { bgcolor: 'darkred' },
                                        position: 'absolute',
                                        right: 4,
                                        top: 4
                                    }}
                                >
                                    <DeleteOutlineOutlined fontSize="small" />
                                </IconButton>
                            </Box>
                        </Grid2>
                    ))}

                    {value.map((src, index) => (
                        <Grid2 key={`s3-${index}`}>
                            <Box className={classes.boxImageContainer} sx={{ position: 'relative' }}>
                                <ImageItem
                                    alt={`s3-preview ${index}`}
                                    className={classes.imgPreview}
                                    onClick={() => handleToggleGallery(index + s3keys.length)}
                                    src={src.raw_file}
                                />
                                <IconButton
                                    size="small"
                                    onClick={() => removeImage(index)}
                                    sx={{
                                        position: 'absolute',
                                        top: 4,
                                        right: 4,
                                        bgcolor: 'red',
                                        color: 'white',
                                        '&:hover': { bgcolor: 'darkred' }
                                    }}
                                >
                                    <DeleteOutlineOutlined fontSize="small" />
                                </IconButton>
                            </Box>
                        </Grid2>
                    ))}
                </Grid2>
            )}

            {showImageDialog && (
                <ImageVideoPickerDialog
                    title={_t('mobile_field_image_picker_dialog_title')}
                    onSelect={handleOnSelect}
                />
            )}

            {openCamera && (
                <Backdrop open sx={{ zIndex: (theme) => theme.zIndex.drawer + 1 }}>
                    <Webcam
                        mode="photo"
                        onCapture={async (blob: Blob) => {
                            const extension = blob.type.split('/')[1] || 'jpg';
                            const fileName = `capture_${Date.now()}.${extension}`;

                            const file = new File([blob], fileName, { type: blob.type });

                            const previewUrl = URL.createObjectURL(file);
                            const fileToUpload = {
                                name: file.name,
                                raw_file: previewUrl,
                                size: file.size,
                                type: file.type
                            };

                            const uniqueFileId = createUUID();
                            const extensionFile = MIME_TYPE_TO_EXTENSION[file.type];
                            const zenputFiles = new ZenputFiles([
                                {
                                    id: uniqueFileId,
                                    mimetype: extensionFile,
                                    name: file.name,
                                    raw_file: file,
                                    size: file.size,
                                    url_base64: previewUrl
                                }
                            ]);

                            let keys;
                            try {
                                keys = await uploadFollowUpFiles(zenputFiles);
                                handlePendingAddS3Keys(keys);
                                onChange([...value, ...[fileToUpload]]);
                            } catch (error) {
                                enqueueSnackbar(_t('mobile_dialog_image_preview_message'), {
                                    variant: 'error'
                                });
                                keys = [];
                            }

                            toggleOpenCamera(false);
                        }}
                        onGoBack={() => {
                            toggleOpenCamera(false);
                        }}
                    />
                </Backdrop>
            )}
        </Stack>
    );
};
